import React, { useState } from "react";
import { X, Smartphone, Monitor, ExternalLink } from "lucide-react";
import { htmlForSector, subjectForSector } from "./televentaEmailTemplate";

/**
 * Vista previa REALISTA del email de televenta: mismo HTML que se manda de verdad por
 * Resend (mismas URLs de S3 para logo/miniatura/vídeo, mismo CTA a la landing con UTM).
 * Al pulsar la miniatura se abre el vídeo real; al pulsar el CTA se abre la landing real —
 * exactamente lo que verá el lead, para que el vendedor sepa qué está enviando.
 *
 * overlayZIndex sigue el patrón de FlowPreviewModal/SendFlowModal (modales anidados).
 */
export default function EmailPreviewModal({
  open,
  onClose,
  sector,
  nombre,
  product = "crm",
  overlayZIndex = 70,
}) {
  const [device, setDevice] = useState("mobile"); // "mobile" | "desktop"

  if (!open) return null;

  const html = htmlForSector(sector, nombre || "Tu Negocio", product);
  const subject = subjectForSector(sector, product);
  const frameWidth = device === "mobile" ? 390 : 700;

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4"
      style={{ zIndex: overlayZIndex }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0">
            <div className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
              Vista previa del email ({product}) · {sector}
            </div>
            <div className="text-sm font-bold text-gray-800 truncate">{subject}</div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              <button
                type="button"
                onClick={() => setDevice("mobile")}
                className={`p-1.5 rounded-lg transition-colors ${
                  device === "mobile" ? "bg-white shadow text-indigo-600" : "text-gray-400 hover:text-gray-600"
                }`}
                title="Vista móvil"
              >
                <Smartphone size={16} />
              </button>
              <button
                type="button"
                onClick={() => setDevice("desktop")}
                className={`p-1.5 rounded-lg transition-colors ${
                  device === "desktop" ? "bg-white shadow text-indigo-600" : "text-gray-400 hover:text-gray-600"
                }`}
                title="Vista ordenador"
              >
                <Monitor size={16} />
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 flex justify-center p-6">
          <div
            className="bg-white shadow-xl transition-all duration-200"
            style={{ width: frameWidth, maxWidth: "100%" }}
          >
            <iframe
              title="Vista previa del email de televenta"
              srcDoc={html}
              style={{ width: "100%", height: 900, border: "none", display: "block" }}
              sandbox="allow-popups allow-popups-to-escape-sandbox"
            />
          </div>
        </div>

        <div className="px-5 py-3 border-t border-gray-100 text-xs text-gray-400 flex items-center gap-1.5 shrink-0">
          <ExternalLink size={12} />
          Realista: la miniatura abre el vídeo real y el botón lleva a la landing real (se abren en pestaña nueva).
        </div>
      </div>
    </div>
  );
}
